/** @module @liquidefy/code-editor/themes/identity/brand @version 1.0.0 @license LQD Research LLC */

/**
 * Liquidefy Brand Definition — the source of truth for Liquidefy's
 * visual identity within the code editor.
 *
 * Defines colors, typography, and spacing as pure data.
 * All derived colors use OKLCH perceptual color science.
 *
 * Pure functions. No DOM, no side effects.
 *
 * @rust brand.rs — false; 4/2/26; sm
 */

// ═══ §1 IMPORTS ═══

import {
  hexToOKLCH,
  oklchToHex,
  setLightness,
  setChroma,
  desaturate,
  rotateHue,
} from '../color/oklch.js';
import type { OKLCH } from '../color/oklch.js';

// ═══ §2 TYPES ═══

/**
 * Brand color system. The accent is #60a5fa — used SPARINGLY
 * for active/focused interactive elements only.
 */
export interface BrandColors {
  readonly accent: string;
  readonly accentMuted: string;
  readonly accentSubtle: string;
  // Semantic
  readonly success: string;
  readonly warning: string;
  readonly error: string;
  readonly info: string;
  // 11-step accent scale (50-950)
  readonly accentScale: Readonly<Record<number, string>>;
}

/**
 * Typography system built around geometric-soft sans-serif fonts.
 */
export interface BrandTypography {
  readonly fontFamily: {
    readonly ui: string;
    readonly heading: string;
    readonly code: string;
    readonly mono: string;
  };
  readonly fontSize: {
    readonly xs: number;
    readonly sm: number;
    readonly base: number;
    readonly md: number;
    readonly lg: number;
    readonly xl: number;
    readonly xxl: number;
  };
  readonly fontWeight: {
    readonly light: number;
    readonly regular: number;
    readonly medium: number;
    readonly semibold: number;
    readonly bold: number;
  };
  readonly lineHeight: {
    readonly tight: number;
    readonly normal: number;
    readonly relaxed: number;
    readonly code: number;
  };
  readonly letterSpacing: {
    readonly tight: string;
    readonly normal: string;
    readonly wide: string;
  };
}

/**
 * Spacing system: border radii and gap scale.
 */
export interface BrandSpacing {
  readonly borderRadius: {
    readonly none: number;
    readonly sm: number;
    readonly md: number;
    readonly lg: number;
    readonly xl: number;
    readonly full: number;
  };
  readonly gap: {
    readonly xs: number;
    readonly sm: number;
    readonly md: number;
    readonly lg: number;
    readonly xl: number;
    readonly xxl: number;
  };
}

/**
 * The complete Liquidefy brand identity.
 */
export interface LiquidelyBrand {
  readonly colors: BrandColors;
  readonly typography: BrandTypography;
  readonly spacing: BrandSpacing;
}

// ═══ §3 INTERNAL HELPERS ═══

/** Clamp to [min, max] */
function clamp(x: number, min: number, max: number): number {
  return x < min ? min : x > max ? max : x;
}

/**
 * Generate an 11-step lightness scale from a seed hex in OKLCH.
 * Steps: 50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950.
 * Lightness ranges from 0.97 (step 50) down to 0.15 (step 950).
 */
function generateAccentScale(seedHex: string): Record<number, string> {
  const seed = hexToOKLCH(seedHex);
  const steps = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
  const lightnesses = [0.97, 0.93, 0.87, 0.78, 0.68, 0.60, 0.52, 0.44, 0.36, 0.27, 0.20];

  const scale: Record<number, string> = {};
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]!;
    const L = lightnesses[i]!;
    // Chroma peaks at mid-lightness, falls off at extremes
    const chromaFactor = 1 - 1.6 * Math.pow(L - 0.55, 2);
    const C = clamp(seed.C * Math.max(0.2, chromaFactor), 0, 0.35);
    scale[step] = oklchToHex({ L, C, H: seed.H });
  }

  return scale;
}

/**
 * Derive a semantic color from the accent hue via OKLCH hue rotation.
 * The semantic colors share the accent's perceptual qualities but with
 * harmonically related hues.
 */
function deriveSemanticColor(accentOklch: OKLCH, hueOffset: number, lightness: number, chroma: number): string {
  const hue = ((accentOklch.H + hueOffset) % 360 + 360) % 360;
  return oklchToHex({ L: lightness, C: clamp(chroma, 0, 0.35), H: hue });
}

// ═══ §4 BRAND CREATION ═══

/**
 * THE Liquidefy brand definition. Returns the complete visual identity.
 *
 * - Accent: #60a5fa (OKLCH ≈ L:0.72, C:0.14, H:250°)
 * - Semantic colors derived via OKLCH harmony from the accent hue
 * - 11-step accent scale generated via lightness ramp
 * - Geometric-soft sans-serif typography stack
 * - Consistent spacing scale
 */
export function createBrand(): LiquidelyBrand {
  const accentHex = '#60a5fa';
  const accentOklch = hexToOKLCH(accentHex);

  // Muted: reduced chroma, slightly lower lightness
  const accentMuted = oklchToHex({
    L: accentOklch.L - 0.08,
    C: clamp(accentOklch.C * 0.5, 0, 0.35),
    H: accentOklch.H,
  });

  // Subtle: very low chroma, high lightness (for backgrounds)
  const accentSubtle = oklchToHex({
    L: 0.92,
    C: clamp(accentOklch.C * 0.15, 0, 0.1),
    H: accentOklch.H,
  });

  // Semantic colors derived from accent hue via harmony relationships
  // Success: accent hue + 135° → emerald-green range (~25° in OKLCH green territory)
  const success = deriveSemanticColor(accentOklch, 135, 0.65, 0.18);
  // Warning: accent hue + 170° → amber range
  const warning = deriveSemanticColor(accentOklch, 170, 0.78, 0.17);
  // Error: accent hue - 225° → rose-red range
  const error = deriveSemanticColor(accentOklch, -225, 0.62, 0.22);
  // Info: same as accent (blue is info)
  const info = accentHex;

  const colors: BrandColors = {
    accent: accentHex,
    accentMuted,
    accentSubtle,
    success,
    warning,
    error,
    info,
    accentScale: generateAccentScale(accentHex),
  };

  const typography: BrandTypography = {
    fontFamily: {
      ui: "'General Sans', 'Plus Jakarta Sans', system-ui, sans-serif",
      heading: "'General Sans', 'Plus Jakarta Sans', system-ui, sans-serif",
      code: "'Berkeley Mono', 'JetBrains Mono', 'Fira Code', monospace",
      mono: "'Berkeley Mono', 'SF Mono', 'Cascadia Mono', monospace",
    },
    fontSize: {
      xs: 11,
      sm: 12,
      base: 13,
      md: 14,
      lg: 16,
      xl: 20,
      xxl: 24,
    },
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.2,
      normal: 1.5,
      relaxed: 1.7,
      code: 1.6,
    },
    letterSpacing: {
      tight: '-0.01em',
      normal: '0',
      wide: '0.02em',
    },
  };

  const spacing: BrandSpacing = {
    borderRadius: {
      none: 0,
      sm: 4,
      md: 6,
      lg: 8,
      xl: 12,
      full: 9999,
    },
    gap: {
      xs: 2,
      sm: 4,
      md: 8,
      lg: 12,
      xl: 16,
      xxl: 24,
    },
  };

  return { colors, typography, spacing };
}

// ═══ §5 ACCESSORS ═══

/**
 * Get a named brand color.
 */
export function getBrandColor(brand: LiquidelyBrand, name: keyof BrandColors): string {
  const val = brand.colors[name];
  if (typeof val === 'string') return val;
  // accentScale is a Record, return the 500 (primary) step
  return (val as Readonly<Record<number, string>>)[500] ?? brand.colors.accent;
}

/**
 * Get a font family string by role.
 */
export function getBrandFont(brand: LiquidelyBrand, role: keyof BrandTypography['fontFamily']): string {
  return brand.typography.fontFamily[role];
}

// ═══ §6 CSS CUSTOM PROPERTY GENERATION ═══

/**
 * Convert ALL brand values to CSS custom properties.
 * Returns a flat map of `--lqd-{category}-{name}: value` entries.
 */
export function brandToCSS(brand: LiquidelyBrand): Record<string, string> {
  return {
    ...brandColorsToCSS(brand),
    ...brandTypographyToCSS(brand),
    ...brandSpacingToCSS(brand),
  };
}

/**
 * Convert brand colors to CSS custom properties.
 */
function brandColorsToCSS(brand: LiquidelyBrand): Record<string, string> {
  const vars: Record<string, string> = {
    '--lqd-color-accent': brand.colors.accent,
    '--lqd-color-accent-muted': brand.colors.accentMuted,
    '--lqd-color-accent-subtle': brand.colors.accentSubtle,
    '--lqd-color-success': brand.colors.success,
    '--lqd-color-warning': brand.colors.warning,
    '--lqd-color-error': brand.colors.error,
    '--lqd-color-info': brand.colors.info,
  };

  // Accent scale
  const scaleSteps = Object.keys(brand.colors.accentScale);
  for (let i = 0; i < scaleSteps.length; i++) {
    const step = scaleSteps[i]!;
    vars[`--lqd-color-accent-${step}`] = (brand.colors.accentScale as Record<string, string>)[step]!;
  }

  return vars;
}

/**
 * Convert brand typography to CSS custom properties.
 */
export function brandTypographyToCSS(brand: LiquidelyBrand): Record<string, string> {
  const t = brand.typography;
  return {
    '--lqd-font-ui': t.fontFamily.ui,
    '--lqd-font-heading': t.fontFamily.heading,
    '--lqd-font-code': t.fontFamily.code,
    '--lqd-font-mono': t.fontFamily.mono,
    '--lqd-font-size-xs': `${t.fontSize.xs}px`,
    '--lqd-font-size-sm': `${t.fontSize.sm}px`,
    '--lqd-font-size-base': `${t.fontSize.base}px`,
    '--lqd-font-size-md': `${t.fontSize.md}px`,
    '--lqd-font-size-lg': `${t.fontSize.lg}px`,
    '--lqd-font-size-xl': `${t.fontSize.xl}px`,
    '--lqd-font-size-xxl': `${t.fontSize.xxl}px`,
    '--lqd-font-weight-light': `${t.fontWeight.light}`,
    '--lqd-font-weight-regular': `${t.fontWeight.regular}`,
    '--lqd-font-weight-medium': `${t.fontWeight.medium}`,
    '--lqd-font-weight-semibold': `${t.fontWeight.semibold}`,
    '--lqd-font-weight-bold': `${t.fontWeight.bold}`,
    '--lqd-line-height-tight': `${t.lineHeight.tight}`,
    '--lqd-line-height-normal': `${t.lineHeight.normal}`,
    '--lqd-line-height-relaxed': `${t.lineHeight.relaxed}`,
    '--lqd-line-height-code': `${t.lineHeight.code}`,
    '--lqd-letter-spacing-tight': t.letterSpacing.tight,
    '--lqd-letter-spacing-normal': t.letterSpacing.normal,
    '--lqd-letter-spacing-wide': t.letterSpacing.wide,
  };
}

/**
 * Convert brand spacing to CSS custom properties.
 */
export function brandSpacingToCSS(brand: LiquidelyBrand): Record<string, string> {
  const s = brand.spacing;
  return {
    '--lqd-radius-none': `${s.borderRadius.none}px`,
    '--lqd-radius-sm': `${s.borderRadius.sm}px`,
    '--lqd-radius-md': `${s.borderRadius.md}px`,
    '--lqd-radius-lg': `${s.borderRadius.lg}px`,
    '--lqd-radius-xl': `${s.borderRadius.xl}px`,
    '--lqd-radius-full': `${s.borderRadius.full}px`,
    '--lqd-gap-xs': `${s.gap.xs}px`,
    '--lqd-gap-sm': `${s.gap.sm}px`,
    '--lqd-gap-md': `${s.gap.md}px`,
    '--lqd-gap-lg': `${s.gap.lg}px`,
    '--lqd-gap-xl': `${s.gap.xl}px`,
    '--lqd-gap-xxl': `${s.gap.xxl}px`,
  };
}
